import { Component } from '@theme/component';

/**
 * @typedef {Object} SfPressLogosRefs
 * @property {HTMLButtonElement[]} brands - Logo brand buttons
 * @property {HTMLElement[]} quotes - Quote elements
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** @extends {Component<SfPressLogosRefs>} */
class SfPressLogos extends Component {
  /** @type {number} */
  #dragStartX = 0;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {number} */
  #autoplayTimer = 0;
  /** @type {number} */
  #autoplayIntervalMs = 0;
  /** @type {boolean} */
  #autoplayEnabled = false;
  /** @type {boolean} */
  #autoplayPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('touchstart', this.#handleDragStart, { passive: true });
    this.addEventListener('touchend', this.#handleDragEnd, { passive: true });
    this.addEventListener('mousedown', this.#handleDragStart);
    this.addEventListener('mouseup', this.#handleDragEnd);

    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
    this.#autoplayEnabled = this.dataset.autoplay === 'true' && !reducedMotion;
    this.#autoplayIntervalMs = Math.max(1, parseFloat(this.dataset.autoplayInterval || '4')) * 1000;

    if (this.#autoplayEnabled && this.refs.brands?.length > 1) {
      this.addEventListener('pointerenter', this.#pauseAutoplay);
      this.addEventListener('pointerleave', this.#resumeAutoplay);
      this.addEventListener('focusin', this.#pauseAutoplay);
      this.addEventListener('focusout', this.#resumeAutoplay);
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('touchstart', this.#handleDragStart);
    this.removeEventListener('touchend', this.#handleDragEnd);
    this.removeEventListener('mousedown', this.#handleDragStart);
    this.removeEventListener('mouseup', this.#handleDragEnd);
    this.removeEventListener('pointerenter', this.#pauseAutoplay);
    this.removeEventListener('pointerleave', this.#resumeAutoplay);
    this.removeEventListener('focusin', this.#pauseAutoplay);
    this.removeEventListener('focusout', this.#resumeAutoplay);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    this.#stopAutoplay();
  }

  /** @param {TouchEvent | MouseEvent} event */
  #handleDragStart = (event) => {
    this.#dragStartX = event instanceof MouseEvent ? event.clientX : event.touches[0]?.clientX ?? 0;
    this.#isDragging = true;
  };

  /** @param {TouchEvent | MouseEvent} event */
  #handleDragEnd = (event) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    const endX = event instanceof MouseEvent ? event.clientX : event.changedTouches[0]?.clientX ?? 0;
    const deltaX = endX - this.#dragStartX;
    if (Math.abs(deltaX) < 50) return;

    const currentIdx = this.refs.brands.findIndex((b) =>
      b.classList.contains('press-logos__brand--active')
    );
    const direction = deltaX < 0 ? 1 : -1;
    const nextIdx = Math.max(0, Math.min(this.refs.brands.length - 1, currentIdx + direction));

    if (nextIdx !== currentIdx) {
      this.#goTo(nextIdx, direction);
      this.#restartAutoplay();
    }
  };

  /** Called by on:click="/selectBrand/index" */
  selectBrand(/** @type {number} */ index) {
    const idx = Number(index);
    const currentIdx = this.refs.brands.findIndex((b) =>
      b.classList.contains('press-logos__brand--active')
    );
    this.#goTo(idx, idx >= currentIdx ? 1 : -1);
    this.#restartAutoplay();
  }

  #startAutoplay = () => {
    if (!this.#autoplayEnabled || this.#autoplayPaused) return;
    this.#stopAutoplay();
    this.#autoplayTimer = window.setInterval(this.#advanceAutoplay, this.#autoplayIntervalMs);
  };

  #stopAutoplay = () => {
    if (this.#autoplayTimer) {
      clearInterval(this.#autoplayTimer);
      this.#autoplayTimer = 0;
    }
  };

  /** Resets the timer so it always waits the full interval after a manual change. */
  #restartAutoplay = () => {
    if (!this.#autoplayEnabled || this.#autoplayPaused) return;
    this.#stopAutoplay();
    this.#startAutoplay();
  };

  #pauseAutoplay = () => {
    this.#autoplayPaused = true;
    this.#stopAutoplay();
  };

  #resumeAutoplay = () => {
    this.#autoplayPaused = false;
    this.#startAutoplay();
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pauseAutoplay();
    else this.#resumeAutoplay();
  };

  #advanceAutoplay = () => {
    const currentIdx = this.refs.brands.findIndex((b) =>
      b.classList.contains('press-logos__brand--active')
    );
    const nextIdx = (currentIdx + 1) % this.refs.brands.length;
    this.#goTo(nextIdx, 1);
  };

  /**
   * @param {number} idx
   * @param {number} direction - 1 = forward, -1 = backward
   */
  #goTo(idx, direction) {
    const enterClass = direction > 0 ? 'press-logos__quote--slide-right' : 'press-logos__quote--slide-left';

    this.refs.brands.forEach((brand, i) => {
      const isActive = i === idx;
      brand.classList.toggle('press-logos__brand--active', isActive);
      brand.setAttribute('aria-pressed', String(isActive));
    });

    this.refs.quotes.forEach((quote, i) => {
      const isActive = i === idx;
      quote.classList.remove('press-logos__quote--slide-right', 'press-logos__quote--slide-left');
      quote.classList.toggle('press-logos__quote--active', isActive);
      quote.setAttribute('aria-hidden', String(!isActive));
      if (isActive) quote.classList.add(enterClass);
    });
  }
}

if (!customElements.get('sf-press-logos')) {
  customElements.define('sf-press-logos', SfPressLogos);
}
